// Fiches de paie des employés (liste tenue par les RH) : salaire, catégorie, n° IPRES, coordonnées de paiement.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem, Snackbar, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { EditOutlined, PaymentsOutlined } from '@mui/icons-material';
import Layout from '../../components/Layout';
import { Kpi, Panel } from '../../components/ui';
import { comptaAPI } from '../../services/api';
import { apiError, fcfa, PAYMENT_MODES } from '../../utils/format';
import { tokens } from '../../theme';

const num = (v) => Number(String(v ?? '').replace(/[\s  ]/g, '')) || 0;

const Salaires = () => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(() => { comptaAPI.salaires().then((r) => setRows(r.data)).catch((e) => setError(apiError(e))); }, []);
  useEffect(() => { load(); }, [load]);

  const open = (r) => setF({
    r, salaire_base: r.salaire_base ? String(r.salaire_base) : '', indemnites_fixes: r.indemnites_fixes ? String(r.indemnites_fixes) : '',
    mode_paiement: r.mode_paiement, banque: r.banque || '', compte: r.compte || '', categorie: r.categorie || '', numero_ipres: r.numero_ipres || '', error: '',
  });
  const save = async () => {
    try {
      const { r, error: _e, ...v } = f;
      await comptaAPI.saveSalaire(r.id, { ...v, salaire_base: num(v.salaire_base), indemnites_fixes: num(v.indemnites_fixes) });
      setF(null); setToast(`Fiche de ${r.prenom} ${r.nom} enregistrée`); load();
    } catch (e) { setF({ ...f, error: apiError(e) }); }
  };
  const masse = rows.reduce((s, r) => s + r.salaire_base + r.indemnites_fixes, 0);
  const sansSalaire = rows.filter((r) => !r.salaire_base).length;
  const field = (k, label, props = {}) => (
    <TextField fullWidth size="small" label={label} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} {...props} />
  );

  return (
    <Layout title="Salaires" plain>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}><Kpi icon={PaymentsOutlined} tint="navy" label="Masse salariale fixe" value={fcfa(masse)} sub="FCFA par mois (base + indemnités)" valueSize={{ xs: 22, sm: 26 }} /></Grid>
        <Grid item xs={6} sm={3} md={2}><Kpi compact label="Employés" value={rows.length} sub="Fiches RH actives" /></Grid>
        <Grid item xs={6} sm={3} md={2}><Kpi compact label="Sans salaire" value={sansSalaire} sub="À renseigner" valueColor={sansSalaire ? '#B7791F' : undefined} /></Grid>
      </Grid>
      <Panel flush>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employé</TableCell><TableCell>Catégorie</TableCell><TableCell align="right">Salaire de base</TableCell>
                <TableCell align="right">Indemnités</TableCell><TableCell>Paiement</TableCell><TableCell>N° IPRES</TableCell><TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => open(r)}>
                  <TableCell><b>{r.nom} {r.prenom}</b><br /><span style={{ fontSize: 12, color: '#5B6675' }}>{r.matricule}{r.poste ? ` · ${r.poste}` : ''}</span></TableCell>
                  <TableCell>{r.categorie || <span style={{ color: '#9AA3AF' }}>—</span>}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 600, color: r.salaire_base ? undefined : 'warning.main' }}>{r.salaire_base ? fcfa(r.salaire_base) : 'à saisir'}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{fcfa(r.indemnites_fixes)}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{PAYMENT_MODES[r.mode_paiement]}{r.banque ? <><br /><span style={{ color: '#5B6675' }}>{r.banque}</span></> : null}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{r.numero_ipres || <span style={{ color: '#9AA3AF' }}>—</span>}</TableCell>
                  <TableCell padding="checkbox">
                    <Tooltip title="Modifier"><IconButton size="small" aria-label={`Modifier la fiche de ${r.nom}`} onClick={(e) => { e.stopPropagation(); open(r); }}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>Aucun employé : les fiches sont créées par les RH.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Ces informations sont reprises sur les bulletins de paie (menu Paie du mois).
      </Typography>

      <Dialog open={!!f} onClose={() => setF(null)} fullWidth maxWidth="sm">
        {f && (
          <>
            <DialogTitle>Fiche de paie — {f.r.prenom} {f.r.nom}</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
              {f.error && <Alert severity="error" sx={{ mb: 2 }}>{f.error}</Alert>}
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} sm={6}>{field('salaire_base', 'Salaire de base mensuel (FCFA)', { inputProps: { inputMode: 'numeric' } })}</Grid>
                <Grid item xs={12} sm={6}>{field('indemnites_fixes', 'Indemnités fixes (FCFA)', { inputProps: { inputMode: 'numeric' }, helperText: 'Transport, logement…' })}</Grid>
                <Grid item xs={12} sm={6}>{field('categorie', 'Catégorie professionnelle', { helperText: 'Ex. : 5e catégorie, agent de maîtrise' })}</Grid>
                <Grid item xs={12} sm={6}>{field('numero_ipres', 'N° IPRES du salarié')}</Grid>
                <Grid item xs={12} sm={4}>
                  {field('mode_paiement', 'Mode de paiement', { select: true, children: Object.entries(PAYMENT_MODES).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>) })}
                </Grid>
                <Grid item xs={12} sm={4}>{field('banque', 'Banque / opérateur')}</Grid>
                <Grid item xs={12} sm={4}>{field('compte', 'RIB / n° de compte')}</Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setF(null)}>Annuler</Button>
              <Button variant="contained" onClick={save}>Enregistrer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Layout>
  );
};

export default Salaires;
